import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

const inp = "w-28 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-right text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF]";
const inr = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Admin-only price edit on an unpaid invoice. Tax is recomputed on the invoice's own stored rates.
export default function InvoicePriceDialog({ invoice, onClose, onSaved }) {
  const lines = invoice?.line_items || [];
  const [amts, setAmts] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAmts(lines.map((l) => String(l.amount ?? 0))); setReason(""); }, [invoice]); // eslint-disable-line
  if (!invoice) return null;

  const cg = Number(invoice.cgst_pct) || 0, sg = Number(invoice.sgst_pct) || 0, ig = Number(invoice.igst_pct) || 0;
  const subtotal = r2(amts.reduce((s, a) => s + (Number(a) || 0), 0));
  const tax = r2(subtotal * cg / 100) + r2(subtotal * sg / 100) + r2(subtotal * ig / 100);
  const total = r2(subtotal + tax);
  const changed = lines.some((l, i) => r2(l.amount) !== r2(amts[i]));
  const bad = amts.some((a) => a === "" || Number(a) < 0 || Number.isNaN(Number(a)));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/v2/invoice-edit/${invoice.invoice_number}/price`, { amounts: amts.map(Number), reason });
      toast.success("Price updated");
      onSaved && onSaved(total);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update the price");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="invoice-price-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Edit price</h2>
            <p className="text-xs text-slate-500">{invoice.invoice_number} · unpaid invoices only. GST is recalculated on this invoice's own tax rate. Every change is logged.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-500 uppercase"><th className="text-left py-1">Line</th><th className="text-right py-1">Was</th><th className="text-right py-1">New amount (before GST)</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-2 pr-2"><p className="font-medium text-slate-800">{l.description}</p>{l.detail && <p className="text-xs text-slate-500">{l.detail}</p>}</td>
                  <td className="py-2 text-right text-slate-500">{inr(l.amount)}</td>
                  <td className="py-2 text-right"><input type="number" min="0" step="0.01" className={inp} value={amts[i] ?? ""} onChange={(e) => setAmts(amts.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`price-line-${i}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="mt-4 ml-auto w-64 text-sm space-y-1">
            <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd>{inr(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">GST ({ig ? `IGST ${ig}%` : `CGST ${cg}% + SGST ${sg}%`})</dt><dd>{inr(tax)}</dd></div>
            <div className="flex justify-between font-bold border-t border-slate-200 pt-1"><dt>New total</dt><dd data-testid="price-new-total">{inr(total)}</dd></div>
            <div className="flex justify-between text-xs text-slate-500"><dt>Current total</dt><dd>{inr(invoice.total_amount)}</dd></div>
          </dl>
          <label className="block mt-4">
            <span className="text-xs font-semibold text-slate-600">Reason for the price change (required, kept in the history)</span>
            <input className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Final Delhivery B2B freight after weight reconciliation" data-testid="price-reason" />
          </label>
          <p className="mt-3 text-xs text-slate-500">This changes the invoice only. It does not change the wallet ledger or the shipment's billed amount.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !changed || bad || !reason.trim()} data-testid="price-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save new price"}
          </Button>
        </div>
      </div>
    </div>
  );
}
